import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell, fetchWidget, formatCount, elapsed } from "./base";

type GitHubWidget = Extract<ProfileConfig["widgets"][number], { type: "github" }>;

type LatestCommitData = {
  user: string;
  repo?: string;
  createdAt?: string;
  lastCommit: { sha: string; message: string } | null;
};

type StatsData = {
  user: string;
  avatarUrl: string;
  name: string | null;
  publicRepos: number;
  followers: number;
  mode: string;
};

export function mountGithubWidget(cfg: GitHubWidget): HTMLElement {
  const title = `GitHub · ${cfg.username}`;
  const shell = widgetShell({ kind: "github", title });
  shell.setLoading();

  const controller = new AbortController();
  shell.root.addEventListener("bio:teardown", () => controller.abort());

  const render = async () => {
    const env = await fetchWidget<LatestCommitData | StatsData>(
      "github",
      { u: cfg.username, mode: cfg.mode },
      { signal: controller.signal },
    );
    if (!env.ok || !env.data) return shell.setError(env.error ?? "unavailable");
    shell.body.innerHTML = "";

    if (cfg.mode === "latest-commit") {
      const d = env.data as LatestCommitData;
      if (!d.lastCommit) {
        shell.body.append(el("div", { class: "widget-github__empty" }, ["no recent push"]));
        return;
      }
      shell.body.append(
        el("a", {
          class: "widget-github__commit",
          href: `https://github.com/${d.repo}/commit/${d.lastCommit.sha}`,
          target: "_blank",
          rel: "noopener noreferrer",
        }, [
          el("code", { class: "widget-github__sha" }, [d.lastCommit.sha]),
          " ",
          el("span", { class: "widget-github__msg" }, [d.lastCommit.message]),
        ]),
        el("div", { class: "widget-github__meta" }, [
          d.repo ?? "",
          " · ",
          d.createdAt ? elapsed(d.createdAt) : "",
        ]),
      );
    } else {
      const d = env.data as StatsData;
      shell.body.append(
        el("div", { class: "widget-github__stats" }, [
          el("span", {}, [`${formatCount(d.publicRepos)} repos`]),
          " · ",
          el("span", {}, [`${formatCount(d.followers)} followers`]),
        ]),
      );
    }
  };

  void render();
  const interval = window.setInterval(render, 5 * 60_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
